/**
 * Ricerca predittiva.
 *
 * Arricchisce un modulo di ricerca che funziona gia' da solo: senza questo
 * script il campo invia a /search e la pagina dei risultati compare
 * normalmente. Lo script non e' un requisito, e' un miglioramento.
 *
 * Usa l'API di ricerca predittiva di Shopify, che e' prima parte: nessuna
 * richiesta a terzi, nessun problema di consenso.
 */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    if (!this.input || !this.results) return;

    this.controller = null;
    this.timer = null;

    this.input.addEventListener('input', () => this.schedule());
    this.input.setAttribute('autocomplete', 'off');
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
  }

  /**
   * Debounce a 250 ms: senza, ogni singolo tasto premuto sarebbe una richiesta
   * di rete, e su connessione mobile si vede.
   */
  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.run(), 250);
  }

  async run() {
    const q = this.input.value.trim();

    if (q.length < 2) {
      this.results.innerHTML = '';
      delete this.dataset.live;
      this.announce('');
      return;
    }

    // Annulla la richiesta precedente: chi scrive in fretta ne genera
    // diverse, e senza abort l'ultima risposta ad arrivare potrebbe non
    // essere quella dell'ultima cosa digitata.
    this.controller?.abort();
    this.controller = new AbortController();

    const params = new URLSearchParams({
      q,
      'resources[type]': 'product,page,article',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      'section_id': 'predictive-results',
    });

    try {
      const response = await fetch(`${this.dataset.searchUrl || '/search'}/suggest?${params}`, {
        signal: this.controller.signal,
      });
      if (!response.ok) return;

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fresh = doc.querySelector('[data-search-results]');

      if (fresh) {
        this.results.innerHTML = fresh.innerHTML;
        const count = this.results.querySelectorAll('[data-search-result]').length;
        // Segnala al CSS che ci sono risultati vivi: senza, la lista statica
        // della ricerca gia' eseguita resterebbe visibile sotto quella nuova,
        // mostrando due elenchi diversi per due ricerche diverse.
        this.dataset.live = '';
        this.announce(count);
      }
    } catch {
      /* Richiesta annullata o rete assente: il modulo resta inviabile. */
    }
  }

  /**
   * Le etichette arrivano come sostantivi puri ("risultato" / "risultati") e
   * il numero lo compone il JavaScript. Passare invece stringhe con un
   * segnaposto significherebbe usare la stessa sintassi di interpolazione di
   * Liquid, che la sostituirebbe prima ancora che lo script la veda.
   */
  announce(count) {
    if (!this.status) return;
    if (count === '') {
      this.status.textContent = '';
      return;
    }
    const noun = count === 1 ? this.dataset.labelOne : this.dataset.labelMany;
    this.status.textContent = `${count} ${noun || ''}`.trim();
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
